import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";

const LoginPage = () => {
  const { theme, toggleTheme, visualTheme } = useTheme();
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleGoogleLogin = () => {
    window.location.href = `${API}/api/auth/google/login`;
  };

  // Theme-specific configurations
  const themeConfig = {
    netset: {
      bgEffects: (
        <>
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-rose-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-rose-500/5 rounded-full blur-3xl" />
        </>
      ),
      logoClass: "h-12 w-auto dark:brightness-0 dark:invert",
      tagline: "Server Monitoring Dashboard",
      taglineClass: "text-muted-foreground text-sm tracking-wide",
      cardStyle: {
        background: theme === 'dark' 
          ? 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.1) 100%)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
        border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
        boxShadow: theme === 'dark' 
          ? '0 20px 40px rgba(0,0,0,0.3)' 
          : '0 20px 40px rgba(0,0,0,0.08)',
      },
      showCorners: false,
      headingClass: "text-xl font-semibold text-foreground",
      headingText: "Welcome Back",
      subheadingClass: "text-muted-foreground text-xs mt-1",
      subheadingText: "Sign in to continue",
      buttonStyle: {
        background: theme === 'dark'
          ? 'linear-gradient(135deg, rgba(244,63,94,0.1), rgba(244,63,94,0.05))'
          : 'linear-gradient(135deg, rgba(244,63,94,0.05), rgba(244,63,94,0.02))',
        border: '1px solid rgba(244,63,94,0.3)',
        color: theme === 'dark' ? '#fb7185' : '#e11d48',
      },
      buttonText: "Continue with Google",
      stats: [
        { value: "Secure", label: "SSL", color: "rose" },
        { value: "OAuth2", label: "AUTH", color: "rose" },
        { value: "Ready", label: "STATUS", color: "emerald" },
      ],
      footerText: "Netset Dashboard",
      footerClass: "text-muted-foreground/50",
      showThemeToggle: true,
    },
    cyber: {
      bgEffects: (
        <>
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-fuchsia-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-cyan-500/10 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-fuchsia-500/5 rounded-full" />
        </>
      ),
      logoClass: "h-12 w-auto brightness-0 invert drop-shadow-[0_0_10px_rgba(0,240,255,0.5)]",
      tagline: "// Server Control Interface",
      taglineClass: "text-cyan-400/80 text-sm font-mono tracking-widest uppercase",
      cardStyle: {
        background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.05) 0%, rgba(255, 0, 255, 0.02) 50%, rgba(10, 15, 30, 0.9) 100%)',
        border: '1px solid rgba(0, 240, 255, 0.2)',
        boxShadow: '0 0 40px rgba(0, 240, 255, 0.1), inset 0 0 40px rgba(0, 0, 0, 0.3)',
      },
      showCorners: true,
      cornerBorderClass: 'border-cyan-500',
      cornerBorderClassAlt: 'border-fuchsia-500',
      headingClass: "text-xl font-bold text-cyan-400",
      headingText: "SYSTEM ACCESS",
      headingStyle: { fontFamily: 'Orbitron, sans-serif' },
      subheadingClass: "text-cyan-400/50 text-xs mt-1 font-mono",
      subheadingText: "Authenticate to continue",
      buttonStyle: {
        background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(255, 0, 255, 0.05))',
        border: '1px solid rgba(0, 240, 255, 0.3)',
        color: '#00F0FF',
      },
      buttonText: "INITIALIZE GOOGLE AUTH",
      stats: [
        { value: "256-bit", label: "ENCRYPT", color: "cyan" },
        { value: "OAuth2", label: "PROTOCOL", color: "fuchsia" },
        { value: "Active", label: "STATUS", color: "green" },
      ],
      footerText: "v2.0 // CYBER INTERFACE",
      footerClass: "text-cyan-400/30 font-mono",
      showThemeToggle: false,
    },
    matrix: {
      bgEffects: (
        <>
          <div className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,0,0.03) 2px, rgba(0,255,0,0.03) 4px)`,
            }}
          />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] border border-green-500/10 rounded-full" />
        </>
      ),
      logoClass: "h-12 w-auto brightness-0 invert drop-shadow-[0_0_10px_rgba(0,255,0,0.5)]",
      tagline: "> TERMINAL_ACCESS_v2.0",
      taglineClass: "text-green-500/80 text-sm font-mono tracking-wider",
      cardStyle: {
        background: 'linear-gradient(135deg, rgba(0, 255, 0, 0.03) 0%, rgba(0, 0, 0, 0.9) 100%)',
        border: '1px solid rgba(0, 255, 0, 0.2)',
        boxShadow: '0 0 40px rgba(0, 255, 0, 0.05), inset 0 0 40px rgba(0, 0, 0, 0.5)',
      },
      showCorners: true,
      cornerBorderClass: 'border-green-500',
      headingClass: "text-xl font-bold text-green-500",
      headingText: "LOGIN_REQUIRED",
      headingStyle: { fontFamily: 'Share Tech Mono, monospace', textShadow: '0 0 10px rgba(0,255,0,0.5)' },
      subheadingClass: "text-green-500/50 text-xs mt-1 font-mono",
      subheadingText: "[ Awaiting authentication... ]",
      buttonStyle: {
        background: 'linear-gradient(135deg, rgba(0, 255, 0, 0.1), rgba(0, 255, 0, 0.02))',
        border: '1px solid rgba(0, 255, 0, 0.3)',
        color: '#00FF00',
      },
      buttonText: ">> AUTHENTICATE_GOOGLE",
      stats: [
        { value: "AES-256", label: "CIPHER", color: "green" },
        { value: "OAuth2", label: "PROTO", color: "green" },
        { value: "ONLINE", label: "NODE", color: "green" },
      ],
      footerText: "[ MATRIX_TERMINAL ]",
      footerClass: "text-green-500/30 font-mono",
      showThemeToggle: false,
    },
  };

  const config = themeConfig[visualTheme] || themeConfig.netset;

  const getStatColorClasses = (color) => {
    const colors = {
      rose: { bg: 'bg-rose-500/5', border: 'border-rose-500/10', text: 'text-rose-400', subtext: 'text-rose-400/40' },
      cyan: { bg: 'bg-cyan-500/5', border: 'border-cyan-500/10', text: 'text-cyan-400', subtext: 'text-cyan-400/40' },
      fuchsia: { bg: 'bg-fuchsia-500/5', border: 'border-fuchsia-500/10', text: 'text-fuchsia-400', subtext: 'text-fuchsia-400/40' },
      green: { bg: 'bg-green-500/5', border: 'border-green-500/10', text: 'text-green-400', subtext: 'text-green-400/40' },
      emerald: { bg: 'bg-emerald-500/5', border: 'border-emerald-500/10', text: 'text-emerald-400', subtext: 'text-emerald-400/40' },
    };
    return colors[color] || colors.rose;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background effects */}
      <div className="absolute inset-0 overflow-hidden">
        {config.bgEffects}
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <img 
              src="/netset-logo.svg" 
              alt="Netset" 
              className={config.logoClass}
            />
          </div>
          <p className={config.taglineClass}>
            {config.tagline}
          </p>
        </div>

        {/* Login Card */}
        <div className="relative">
          {/* Glow effect behind card */}
          {visualTheme === 'cyber' && (
            <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-fuchsia-500 to-cyan-500 rounded-2xl opacity-20 blur" />
          )}
          
          <div 
            className="relative p-8 rounded-2xl space-y-6"
            style={config.cardStyle}
          >
            {/* Corner decorations - only for themes that want them */}
            {config.showCorners && (
              <>
                <div className={`absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 ${config.cornerBorderClass}`} />
                <div className={`absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 ${config.cornerBorderClass}`} />
                <div className={`absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 ${config.cornerBorderClassAlt || config.cornerBorderClass}`} />
                <div className={`absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 ${config.cornerBorderClassAlt || config.cornerBorderClass}`} />
              </>
            )}

            {/* Heading */}
            <div className="text-center mb-4">
              <h2 className={config.headingClass} style={config.headingStyle || {}}>
                {config.headingText}
              </h2>
              <p className={config.subheadingClass}>
                {config.subheadingText}
              </p>
            </div>

            {/* Login Button */}
            <Button
              data-testid="google-login-btn"
              onClick={handleGoogleLogin}
              className="w-full h-12 gap-3 font-semibold tracking-wide"
              style={config.buttonStyle}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className={visualTheme !== 'netset' ? 'uppercase' : ''}>
                {config.buttonText}
              </span>
            </Button>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-2 text-center text-xs font-mono">
              {config.stats.map((stat, idx) => {
                const colorClasses = getStatColorClasses(stat.color);
                return (
                  <div key={idx} className={`p-2 rounded ${colorClasses.bg} border ${colorClasses.border}`}>
                    <div className={colorClasses.text}>{stat.value}</div>
                    <div className={colorClasses.subtext}>{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 space-y-2">
          <p className={`text-xs ${config.footerClass}`}>{config.footerText}</p>
          
          {/* Theme Toggle - Only for Netset */}
          {config.showThemeToggle && (
            <Button
              data-testid="theme-toggle-login"
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="text-muted-foreground text-xs"
            >
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
